/**
 * Client/src/utils/weightUtils.js
 *
 * Centralized weight unit conversion and display formatting.
 * All weight values stored in the database are in grams.
 *
 * Conversion factors:
 *   "lb"  → multiply by 453.592
 *   "oz"  → multiply by 28.3495
 *   "g"   → pass-through
 *   "units" → pass-through (count-based items; no gram equivalent)
 */

const LB_TO_G = 453.592;
const OZ_TO_G = 28.3495;

/**
 * Convert a weight value from any supported unit to grams.
 * 
 * @param {number|string} value - The weight value to convert.
 * @param {string} unit - The unit: "g", "oz", "lb", or "units".
 * @returns {number} The weight in grams (or raw value for "units").
 */
export function toGrams(value, unit) {
    const v = parseFloat(value);
    if (isNaN(v)) return NaN;
    if (!unit || unit === "g" || unit === "units") return v;
    if (unit === "oz") return v * OZ_TO_G;
    if (unit === "lb") return v * LB_TO_G;
    return v;
}

/**
 * Convert a gram value to the specified unit.
 *
 * @param {number|string} grams - The weight in grams.
 * @param {string} unit - Target unit: "g", "oz", or "lb".
 * @returns {number} The weight in the target unit.
 */
export function fromGrams(grams, unit) {
    const g = parseFloat(grams);
    if (isNaN(g)) return NaN;
    if (!unit || unit === "g") return g;
    if (unit === "oz") return g / OZ_TO_G;
    if (unit === "lb") return g / LB_TO_G;
    return g;
}

/**
 * Convert a weight value between any two supported units.
 * Converts through grams as the intermediate representation.
 *
 * @param {number|string} value - The weight value.
 * @param {string} fromUnit - Source unit: "g", "oz", or "lb".
 * @param {string} toUnit - Target unit: "g", "oz", or "lb".
 * @returns {number} The converted weight value.
 */
export function convertWeight(value, fromUnit, toUnit) {
    if (fromUnit === toUnit) return parseFloat(value);
    const grams = toGrams(value, fromUnit);
    if (isNaN(grams)) return NaN;
    return fromGrams(grams, toUnit);
}

/**
 * Format a gram value using the smallest number possible:
 * - If ≥ 1 lb (453.592 g / 16 oz): display in pounds
 * - If ≥ 1 oz (28.3495 g): display in ounces
 * - If < 1 oz: display in grams
 * Decimals shown only when the value is not a whole number (max 2 decimal places).
 *
 * Examples:
 *   formatWeightFromGrams(32)       → "1.13 oz"
 *   formatWeightFromGrams(56.699)   → "2 oz"
 *   formatWeightFromGrams(453.592)  → "1 lb"
 *   formatWeightFromGrams(500)      → "1.1 lb"
 *   formatWeightFromGrams(1000)     → "2.2 lb"
 *   formatWeightFromGrams(5)        → "5 g"
 *   formatWeightFromGrams(0)        → "0 g"
 *   formatWeightFromGrams(null)     → "—"
 *
 * @param {number|string|null|undefined} grams - The weight value in grams.
 * @returns {string} Human-readable weight string.
 */
export function formatWeightFromGrams(grams) {
    const g = parseFloat(grams);
    if (grams == null || isNaN(g)) return "—";
    if (g === 0) return "0 g";

    // Helper: round to 2 decimal places, strip trailing zeros, omit decimals if whole
    const fmt = (val) => {
        const r = Math.round(val * 100) / 100;
        return r % 1 === 0 ? r : parseFloat(r.toFixed(2));
    };

    if (g >= LB_TO_G) return `${fmt(g / LB_TO_G)} lb`;
    if (g >= OZ_TO_G) return `${fmt(g / OZ_TO_G)} oz`;

    return `${fmt(g)} g`;
}
